import {
  Button,
  ButtonGroup,
  DestructiveConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
  useStateWithSetAction,
  VirtualizedDataTable,
  type VirtualizedDataTableProps,
} from "@tellescope/ui";
import { Select } from "@tellescope/design-system";
import { PhoneLogEntry } from "@tellescope/tanstack-query";
import { SetStateAction, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Filter, Trash2 } from "lucide-react";
import { useForm } from "@tellescope/forms";
import { useAppPermissionQuery } from "../../../permissions";
import { usePhoneLogsTable } from "../../../hooks/usePhoneLogsTable";
import { usePhoneLogsDefaultViewShallowStore } from "../../../store";
import { PhoneLogsTableConfigDialog } from "../../../components/phonelogs/PhoneLogsTableConfigDialog";

const VIEWS_PATH = "/settings/phone/logs/views";

export const PhoneLogsTable = () => {
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  const params = useParams();
  const viewId = params.viewId;
  const { lastSelectedViewId, setLastSelectedViewId } = usePhoneLogsDefaultViewShallowStore(
    "lastSelectedViewId",
    "setLastSelectedViewId"
  );

  // useEffect is justified here because this store is a localStorage store that we want to sync to
  useEffect(() => {
    if (viewId && viewId !== lastSelectedViewId) {
      setLastSelectedViewId(viewId);
    }
  }, [viewId, lastSelectedViewId, setLastSelectedViewId]);

  const setViewId = useCallback(
    (idOrUpdater: SetStateAction<string>) => {
      const newId = typeof idOrUpdater === "function" ? idOrUpdater(viewId ?? "all") : idOrUpdater;
      navigate(`${VIEWS_PATH}/${newId}`);
    },
    [navigate, viewId]
  );

  const phoneLogsTable = usePhoneLogsTable({ viewId, setViewId });

  const [isAddViewDialogOpen, setIsAddViewDialogOpen] = useStateWithSetAction(false, (isOpen) => {
    if (!isOpen) {
      addViewForm.resetForm();
    }
  });
  const [viewConfigDialogIsOpen, setViewConfigDialogIsOpen] = useState(false);
  const [deleteDialogIsOpen, setDeleteDialogIsOpen] = useState(false);

  const addViewForm = useForm({
    fields: {
      title: { type: "text", required: true, initialValue: "" },
    },
    validateOn: "blur",
    onSubmit: (values) => {
      if (!values.title) return;

      const saveAsFilter = phoneLogsTable.selectedView.filter;

      phoneLogsTable.createTableViewMutation.mutateAsync(
        {
          page: `${phoneLogsTable.TELLESCOPE_2_TABLE_VIEW_PREFIX}PhoneLogs`,
          title: values.title,
          columns: phoneLogsTable.selectedView.columns,
          ...(saveAsFilter !== undefined ? { filter: saveAsFilter } : undefined),
        },
        {
          onSuccess: (res) => {
            phoneLogsTable.resetDefaultView();
            navigate(`${VIEWS_PATH}/${res.id}`);
          },
        }
      );
      setIsAddViewDialogOpen(false);
    },
  });

  const {
    delete: { access: canDelete },
  } = useAppPermissionQuery({ model: "table_views", actions: ["delete"] });

  const activeFiltersCount = phoneLogsTable.activeFiltersCount;

  if (!viewId) {
    return <Navigate replace to="./all" />;
  }

  const actionButtons: NonNullable<VirtualizedDataTableProps<PhoneLogEntry>["actionButtons"]> = [
    <ButtonGroup key="view-management">
      <Select value={phoneLogsTable.viewId} onValueChange={(value) => navigate(`${VIEWS_PATH}/${value}`)}>
        <Select.Trigger data-trackingid="phone-logs-table-view-selector" className="w-auto min-w-[120px] max-w-[200px]">
          <Select.Value placeholder={t("All")} />
        </Select.Trigger>
        <Select.Content>
          <Select.Item value="all">{t("All")}</Select.Item>
          {phoneLogsTable.views.map((view) => (
            <Select.Item key={view.id} value={view.id}>
              {view.title}
            </Select.Item>
          ))}
        </Select.Content>
      </Select>
      <Button
        variant="outline"
        className="relative h-8 w-8 p-0 border-l-0"
        onClick={() => setViewConfigDialogIsOpen(true)}
        hoverLabel={t("Configure View")}
        data-trackingid="phone-logs-table-configure-view"
      >
        <Filter strokeWidth={1.8} />
        {!!activeFiltersCount && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-primary" />}
      </Button>
      {phoneLogsTable.viewId !== "all" && canDelete && (
        <Button
          variant="outline"
          className="h-8 w-8 p-0 border-l-0"
          onClick={() => setDeleteDialogIsOpen(true)}
          hoverLabel={t("Delete View")}
          data-trackingid="phone-logs-table-delete-view"
        >
          <Trash2 strokeWidth={1.8} className="text-destructive" />
        </Button>
      )}
    </ButtonGroup>,
    ...(phoneLogsTable.isDefaultViewModified
      ? [
          <Button
            key="save-as-view"
            variant="outline"
            size="sm"
            className="font-light text-xs"
            onClick={() => setIsAddViewDialogOpen(true)}
            data-trackingid="phone-logs-table-save-default-as-view"
          >
            {t("Save as View")}
          </Button>,
        ]
      : []),
  ];

  return (
    <div className="flex flex-col flex-1 min-h-0 px-6 pt-4">
      <VirtualizedDataTable
        {...phoneLogsTable.virtualizedTableProps}
        viewportHeight={{ offset: 320 }}
        actionButtons={actionButtons}
        searchPlaceholder={t("Search logs...")}
        hideScrollbar
      />

      <Dialog open={isAddViewDialogOpen} onOpenChange={setIsAddViewDialogOpen}>
        <DialogContent>
          <div className="space-y-1">
            <DialogTitle className="font-medium text-sm">{t("Save as View")}</DialogTitle>
            <DialogDescription className="text-xs">
              {t("Enter a name for the new view. It will be saved with your current column configuration.")}
            </DialogDescription>
          </div>
          <form {...addViewForm.getFormProps()} className="flex flex-col gap-2">
            <Input
              placeholder={t("View name...")}
              {...addViewForm.getFieldProps("title")}
              onKeyDownCapture={(e) => {
                if (e.key === "Enter" && addViewForm.values.title) {
                  e.stopPropagation();
                  addViewForm.submitForm();
                }
              }}
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button onClick={() => setIsAddViewDialogOpen(false)} variant="outline">
                {t("Cancel")}
              </Button>
              <Button
                type="submit"
                disabled={!addViewForm.isValid}
                data-trackingid="phone-logs-table-create-table-view-dialog-confirm"
              >
                {t("Save View")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {!phoneLogsTable.isLoadingViews && (
        <PhoneLogsTableConfigDialog
          title={t("Configure {{tableViewName}}", {
            tableViewName: phoneLogsTable.selectedView.title || t("All"),
          })}
          open={viewConfigDialogIsOpen}
          onOpenChange={(open) => setViewConfigDialogIsOpen(open)}
          columnConfigFormProps={{
            combinedFieldToColumnConfigMap: phoneLogsTable.combinedFieldToColumnConfigMap,
            columnVisibility: phoneLogsTable.columnVisibility,
            orderedVisibleColumnIds: phoneLogsTable.orderedVisibleColumnIds,
          }}
          onApplyConfig={phoneLogsTable.onApplyConfig}
          activeFilters={phoneLogsTable.activeFilters}
          directionOptions={phoneLogsTable.directionOptions}
          statusOptions={phoneLogsTable.statusOptions}
          onClearAll={
            phoneLogsTable.isDefaultViewModified
              ? () => {
                  phoneLogsTable.resetDefaultView();
                  setViewConfigDialogIsOpen(false);
                }
              : undefined
          }
          isDirty={phoneLogsTable.isDefaultViewModified}
        />
      )}

      <DestructiveConfirmDialog
        onConfirm={() => {
          phoneLogsTable.deleteTableViewMutation.mutateAsync(phoneLogsTable.viewId);
          navigate(`${VIEWS_PATH}/all`);
        }}
        loading={phoneLogsTable.deleteTableViewMutation.isPending}
        open={deleteDialogIsOpen}
        onOpenChange={(open) => setDeleteDialogIsOpen(open)}
        description={t("This will permanently delete the view. This action cannot be undone.")}
      />
    </div>
  );
};
